import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { getFriendlyError } from '../utils/friendlyErrors';
import {
  mirrorAuthToIDB,
  clearAuthIDB,
  rehydrateFromIDBIfNeeded,
} from '../utils/authDurability';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Get backend URL from environment
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// =============================================================================
// Auth storage helpers — sessionStorage (runtime, per-tab) + localStorage
// (passport for rehydrating sessionStorage in newly-opened tabs).
//
// The previous implementation used sessionStorage exclusively, which meant
// opening a new tab via cmd+click / right-click → user appeared logged out
// and "data disappeared". sessionStorage doesn't cross tabs.
//
// The new flow:
//   - login() → write to BOTH localStorage and sessionStorage
//   - On mount → if sessionStorage is empty but localStorage has a token,
//     copy localStorage values INTO sessionStorage so all existing
//     `sessionStorage.getItem('token')` reads across the codebase keep working
//     without modification.
//   - logout() → clear BOTH (broadcasted to all tabs via storage event)
//   - storage event listener — if another tab logs out, clear this tab too.
//
// Trade-off: access token is now mildly more XSS-exposed (persists across
// browser restarts vs cleared on tab close). Mitigations: refresh token is
// HttpOnly cookie, access token is short-lived (15 min), React + CSP protect
// against XSS injection. Net: huge UX win, marginal security cost.
// =============================================================================
const AUTH_KEYS = ['user', 'token', 'refresh_token'];

const rehydrateSessionFromLocal = () => {
  // Copy localStorage → sessionStorage on tab mount IFF sessionStorage is empty.
  // Returns the diagnostic state for telemetry.
  if (typeof window === 'undefined') return { rehydrated: false, source: 'no_window' };
  try {
    const sessionHasToken = !!sessionStorage.getItem('token');
    const localHasToken = !!localStorage.getItem('token');
    if (sessionHasToken) return { rehydrated: false, source: 'session_already_set' };
    if (!localHasToken) return { rehydrated: false, source: 'no_local_token' };
    AUTH_KEYS.forEach((k) => {
      const v = localStorage.getItem(k);
      if (v != null) sessionStorage.setItem(k, v);
    });
    // eslint-disable-next-line no-console
    console.info('[Auth] Rehydrated session from localStorage (cross-tab)');
    return { rehydrated: true, source: 'local_storage' };
  } catch (_) {
    return { rehydrated: false, source: 'storage_error' };
  }
};

const persistAuth = (key, value) => {
  // Write the auth value to BOTH stores. Skip localStorage for impersonation
  // tokens (those should stay per-tab so the admin's original session in
  // their other tab isn't replaced).
  try {
    if (value == null) {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    } else {
      sessionStorage.setItem(key, value);
      localStorage.setItem(key, value);
    }
  } catch (_) { /* storage quota — non-blocking */ }
  // iter-230ba — Mirror to IndexedDB for PWA / iOS Safari durability.
  // Fire-and-forget; never blocks the auth flow.
  try { mirrorAuthToIDB({ [key]: value }); } catch (_) { /* ignore */ }
};

const clearAuth = () => {
  AUTH_KEYS.forEach((k) => {
    try { sessionStorage.removeItem(k); } catch (_) { /* ignore */ }
    try { localStorage.removeItem(k); } catch (_) { /* ignore */ }
  });
  // iter-230ba — Also clear the IndexedDB mirror so logout is honoured
  // across PWA shell reboots.
  try { clearAuthIDB(); } catch (_) { /* ignore */ }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // iter-230ak — Stable refs that mirror `user` / `token`. Consumed by
  // `apiCall`, `isAdmin`, `isVendor`, `hasModuleAccess`, `isImpersonating`
  // so those callbacks no longer depend on `user` / `token` state.
  // This eliminates the mount-cascade storm where:
  //   1) AuthProvider hydrated → setUser(...) → user changes →
  //      isAdmin rebuilds → consumer's fetch effect re-fires
  //   2) setToken(...) → token changes → apiCall rebuilds →
  //      consumer's fetch effect re-fires AGAIN
  // Net effect on DashboardNew: 3 sequential `fetchDashboardData`
  // invocations × 2 parallel calls each = 6 API hits during boot,
  // visible to the user as a 403 "refresh storm" when Cloudflare
  // challenges the burst.
  //
  // iter-230al — Eager-initialise from sessionStorage/localStorage at
  // ref creation (lazy initialiser fn) so the FIRST render already has
  // the real values. Previously useEffect-driven sync left tokenRef
  // null for one render cycle; if a child component fired apiCall in
  // its mount-effect (e.g. fresh-signup → /dashboard) the request went
  // out without an Authorization header, the backend returned 403
  // "Not authenticated", and our per-URL 30s backoff kicked in. With
  // eager init, tokenRef is correct before any child even renders.
  const userRef = React.useRef(null);
  const tokenRef = React.useRef(null);
  if (userRef.current === null && typeof window !== 'undefined') {
    try {
      const stored = sessionStorage.getItem('user') || localStorage.getItem('user');
      if (stored) userRef.current = JSON.parse(stored);
    } catch (_) { /* corrupt JSON — leave null */ }
  }
  if (tokenRef.current === null && typeof window !== 'undefined') {
    try {
      tokenRef.current =
        sessionStorage.getItem('token') || localStorage.getItem('token') || null;
    } catch (_) { /* storage error — leave null */ }
  }
  React.useEffect(() => { userRef.current = user; }, [user]);
  React.useEffect(() => { tokenRef.current = token; }, [token]);

  // iter-230ak — Per-endpoint 403 backoff. When Cloudflare (or any
  // upstream) returns 403 we MUST NOT keep retrying the same URL on
  // every render; doing so produces the visible "GET ... 403 ... 403
  // ... 403" log storm users have reported on production. Each entry
  // is keyed by the full URL → epoch-ms `until` value.
  const forbiddenUntilRef = React.useRef(new Map());
  const FORBIDDEN_BACKOFF_MS = 30000; // 30s suppression per endpoint

  // iter-231B — Rate-limit (HTTP 429) and server-error (HTTP 502/503/504)
  // per-URL backoff. Production showed trial-expired vendors generating
  // hundreds of `vendor/stats → 429 → vendor/stats → 429` cycles per
  // minute because the dashboard's useEffect re-fires on every parent
  // re-render and the apiCall layer had no per-URL suppression for these
  // statuses. Each 429 now seeds a `rateLimitedUntilRef` window honoring
  // the server's `Retry-After` header (or a sensible default). Subsequent
  // calls to the same URL short-circuit with a typed RATE_LIMITED error
  // until the window elapses.
  const rateLimitedUntilRef = React.useRef(new Map());
  const serverErrorUntilRef = React.useRef(new Map());
  const RATE_LIMIT_DEFAULT_MS = 30000;       // honor Retry-After if present, else 30s
  const SERVER_ERROR_BACKOFF_MS = 15000;     // 15s suppression for 502/503/504
  const REFRESH_TOKEN_RATE_LIMITED_MS = 10000; // 10s suppression for refresh-token 429

  // iter-230ab — Soft-logout signal: when a vendor's API call gets 401
  // and the refresh fails, we DO NOT force-navigate to /login or wipe
  // `user`. Instead we flip `sessionExpired=true` so the active page
  // can show an inline "session expired — log back in" banner without
  // unmounting. Wiping `user` was the trigger for the dashboard ↔
  // login flicker: PrivateRoute saw `!user` → `<Navigate to=/login>`
  // → Login.jsx's mount-effect (`if (user && role===vendor) navigate(
  // '/dashboard')`) raced against storage-event rehydration in the
  // same tab on PWA WebViews → loop. Setting a non-disruptive flag
  // instead is the single most defensive line in this file.
  const [sessionExpired, setSessionExpired] = useState(false);

  // iter-230ab — Same-tab storage-event guard. Some PWA WebViews
  // (notably Android Chrome's PWA shell) fire `storage` events in the
  // SAME tab that wrote the change, contrary to the spec. Without this
  // guard, logout()'s `localStorage.setItem('auth_logout_broadcast',
  // ...)` immediately fires our own listener and re-runs the logout
  // branch, but the localStorage 'user'/'token' keys are ALSO racing
  // (logout clears them, but the storage event for 'auth_logout_
  // broadcast' may arrive before clearAuth completes). Net effect: we
  // sometimes saw `user` get restored on the same tab milliseconds
  // after logout. The 2-second guard window covers that race.
  const logoutGuardUntilRef = React.useRef(0);

  useEffect(() => {
    // Rehydrate sessionStorage from localStorage if this is a new tab. ALL
    // existing components read from sessionStorage directly (30+ files), so
    // this single line keeps them working unmodified across tab opens.
    rehydrateSessionFromLocal();

    // Check for stored auth data on mount
    const storedUser = sessionStorage.getItem('user');
    const storedToken = sessionStorage.getItem('token');

    if (storedUser && storedToken) {
      try {
        setUser(JSON.parse(storedUser));
        setToken(storedToken);
      } catch (_) {
        // Corrupt user JSON — clear and force re-login
        clearAuth();
      }
      setLoading(false);
    } else {
      // iter-230ba — sessionStorage + localStorage are both empty. Try the
      // IndexedDB durability mirror as a last-ditch rehydration source. This
      // catches PWA installs / iOS Safari purges that wipe Web Storage but
      // leave IndexedDB intact.
      rehydrateFromIDBIfNeeded()
        .then((result) => {
          if (result && result.rehydrated) {
            const u = sessionStorage.getItem('user');
            const t = sessionStorage.getItem('token');
            if (u && t) {
              try {
                setUser(JSON.parse(u));
                setToken(t);
              } catch (_) { clearAuth(); }
            }
          }
        })
        .finally(() => setLoading(false));
    }

    // iter-230at — When a new Service Worker takes control of this
    // page (e.g. push-sw.js activating after the iter-230at stale-SW
    // unregister + reload), the 403s that the old SW was synthesising
    // STOP arriving. Wipe the per-URL forbidden backoff so the next
    // dashboard fetch goes through immediately, instead of waiting
    // 30 s per URL. Symptom this targets: post-deploy vendor sees the
    // first dashboard load 403-storm, the reload kicks in, but
    // sessionStorage backoff entries are still keyed so the recovered
    // dashboard appears blank until the 30 s window expires.
    const onControllerChange = () => {
      try {
        forbiddenUntilRef.current.clear();
        rateLimitedUntilRef.current.clear();
        serverErrorUntilRef.current.clear();
        // eslint-disable-next-line no-console
        console.info('[Auth] SW controller changed — cleared 403/429/5xx backoff');
      } catch (_) { /* ignore */ }
    };
    if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    }
    return () => {
      if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
        try {
          navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
        } catch (_) { /* ignore */ }
      }
    };
  }, []);

  const login = async (userData, accessToken) => {
      const loginTime = new Date().toISOString();
      let userWithLoginTime = { ...userData, lastLogin: loginTime };

      // iter-230p + iter-230aq — Wipe artifact verdict cache too on
      // login. If the prior identity in this tab was stuck mid-onboarding,
      // their stale `completed: false` verdict would otherwise lock the
      // NEW (legitimately complete) vendor into the onboarding loop.
      // Inline + version-list sweep — no dynamic require() (webpack
      // tree-shaking silently dropped that import in prod, re-poisoning
      // the next login with stale state).
      try {
        const ARTIFACT_PREFIXES = ['onboarding_artifacts_v2_', 'onboarding_artifacts_v1_'];
        Object.keys(sessionStorage)
          .filter((k) => ARTIFACT_PREFIXES.some((p) => k.startsWith(p)))
          .forEach((k) => sessionStorage.removeItem(k));
      } catch (_) { /* sessionStorage disabled — nothing to clear */ }

      // For admin/super_admin users, fetch RBAC info (admin_type and accessible modules)
      if (userData.role === 'admin' || userData.role === 'super_admin') {
        try {
          const response = await fetch(`${BACKEND_URL}/api/admin/me/accessible-modules`, {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            }
          });
          if (response.ok) {
            const rbacData = await response.json();
            userWithLoginTime = {
              ...userWithLoginTime,
              admin_type: rbacData.admin_type,
              accessible_modules: rbacData.accessible_modules,
              granular_permissions: rbacData.granular_permissions || null,
              // Only ever true when the backend resolved an explicit Custom
              // Role assignment (Team & Roles > Roles & Permissions) — every
              // other admin/super_admin account keeps seeing the full,
              // unfiltered navigation exactly as before.
              is_restricted_team_member: rbacData.source === 'custom_role',
              is_super_admin: rbacData.is_super_admin || userData.role === 'super_admin'
            };
          }
        } catch (error) {
          console.error('Failed to fetch RBAC info:', error);
          // For super_admin, set the flag even if RBAC fetch fails
          if (userData.role === 'super_admin') {
            userWithLoginTime.is_super_admin = true;
          }
        }
      }
      
      // Save to BOTH sessionStorage (per-tab runtime) AND localStorage (cross-tab passport)
      persistAuth('user', JSON.stringify(userWithLoginTime));
      persistAuth('token', accessToken);
      
      // Then update React state
      setUser(userWithLoginTime);
      setToken(accessToken);
      // iter-230ak / iter-231B — Fresh session → clear any 403/429/5xx
      // backoff from the previous (likely-expired) identity so the first
      // dashboard fetch isn't suppressed by stale per-URL entries.
      try {
        forbiddenUntilRef.current.clear();
        rateLimitedUntilRef.current.clear();
        serverErrorUntilRef.current.clear();
      } catch (_) { /* ignore */ }

      // iter-230ab — A successful login always clears any in-flight
      // session-expired soft-logout state so the inline banner hides
      // and the page returns to normal interactive mode.
      setSessionExpired(false);
      // Arm the same-tab storage guard briefly so the storage-event
      // echo of the persistAuth writes above doesn't bounce.
      logoutGuardUntilRef.current = Date.now() + 1500;
      // Store login history in localStorage (non-sensitive analytics data)
      const loginHistory = JSON.parse(localStorage.getItem('loginHistory') || '[]');
      const device = /Mobile/.test(navigator.userAgent) ? 'Mobile Device' : 'Desktop';
      loginHistory.push({
        userId: userData.id,
        userName: userData.name,
        role: userData.role,
        loginTime: loginTime,
        timestamp: new Date().toISOString(),
        device: device
      });
      // Keep only last 50 logins
      if (loginHistory.length > 50) {
        loginHistory.shift();
      }
      localStorage.setItem('loginHistory', JSON.stringify(loginHistory));
      
      // Give React time to process the state update
      await new Promise(r => setTimeout(r, 50));
  };

  const logout = (redirectTo = null) => {
    // Store the current user role before clearing for proper redirect
    const wasAdmin = user?.role === 'admin' || user?.role === 'super_admin';

    // iter-230p + iter-230aq — Purge artifact verdict on logout. The
    // earlier dynamic `require()` could silently fail under webpack
    // tree-shaking / circular-dep edge cases, leaving stale verdicts
    // that re-poisoned the NEXT login in the same tab — the exact
    // bug behind the iter-230ap subscription loop persisting after
    // logout/login. Inline + version-list = no module dep to fail.
    try {
      const ARTIFACT_PREFIXES = ['onboarding_artifacts_v2_', 'onboarding_artifacts_v1_'];
      Object.keys(sessionStorage)
        .filter((k) => ARTIFACT_PREFIXES.some((p) => k.startsWith(p)))
        .forEach((k) => sessionStorage.removeItem(k));
    } catch (_) { /* sessionStorage disabled — nothing to clear */ }

    setUser(null);
    setToken(null);
    clearAuth();  // clears BOTH sessionStorage and localStorage
    // iter-230ak / iter-231B — Wipe 403/429/5xx backoff so a re-login
    // from this tab gets a clean slate (no stale suppression from the
    // previous identity).
    try {
      forbiddenUntilRef.current.clear();
      rateLimitedUntilRef.current.clear();
      serverErrorUntilRef.current.clear();
    } catch (_) { /* ignore */ }

    // iter-230ab — Arm the same-tab storage-event guard for 2 seconds
    // so the broadcast we're about to fire (and the implicit clears)
    // don't bounce back through our own storage listener.
    logoutGuardUntilRef.current = Date.now() + 2000;

    // Broadcast logout to other tabs via localStorage event
    localStorage.setItem('auth_logout_broadcast', Date.now().toString());
    localStorage.removeItem('auth_logout_broadcast');
    
    // If explicit redirect provided (must be a string), use it
    // Otherwise, redirect admins to admin login and vendors to vendor login
    if (redirectTo && typeof redirectTo === 'string') {
      window.location.href = redirectTo;
    } else if (wasAdmin) {
      window.location.href = '/admin/login';
    }
    // For vendors, let the PrivateRoute handle redirect to /login
  };

  // iter-230ak — Read role through userRef so these callbacks are stable
  // across renders. Consumers (DashboardNew, Sidebar, etc.) put them in
  // their useEffect / useCallback dep arrays; an unstable identity here
  // triggered re-fetches on every auth state change.
  const isAdmin = useCallback(
    () => {
      const r = userRef.current?.role;
      return r === 'admin' || r === 'super_admin';
    },
    []
  );
  const isVendor = useCallback(
    () => userRef.current?.role === 'vendor',
    []
  );
  
  // Cross-tab logout synchronization via localStorage broadcast channel
  React.useEffect(() => {
    const handleStorageChange = (e) => {
      // iter-230ab — Suppress same-tab storage echoes that arrive
      // within the 2-second window after our own logout()/login().
      // PWA WebViews fire `storage` in the originating tab too; without
      // this guard our handler "restores" user from a stale value
      // milliseconds after we cleared it.
      if (Date.now() < logoutGuardUntilRef.current) {
        return;
      }
      if (e.key === 'auth_logout_broadcast') {
        // Another tab logged out — sync logout in this tab
        setUser(null);
        setToken(null);
        clearAuth();
      } else if (e.key === 'token' && e.newValue && e.newValue !== token) {
        // Another tab refreshed/updated the token — adopt it so this tab
        // doesn't fall behind and start sending the old (potentially expired)
        // token to the API. Avoids the "two tabs racing the refresh" pattern.
        try {
          sessionStorage.setItem('token', e.newValue);
          setToken(e.newValue);
        } catch (_) { /* ignore */ }
      } else if (e.key === 'user' && e.newValue) {
        // Another tab updated the user (e.g., onboarding completion) — sync.
        try {
          sessionStorage.setItem('user', e.newValue);
          setUser(JSON.parse(e.newValue));
        } catch (_) { /* ignore */ }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [token]);
  
  // Update user data in context and BOTH stores (e.g., after onboarding completes)
  const updateUser = (updates) => {
    setUser(prev => {
      const updated = { ...prev, ...updates };
      persistAuth('user', JSON.stringify(updated));
      return updated;
    });
  };

  // Check if user is super admin (for RBAC)
  const isSuperAdmin = user?.role === 'super_admin' || (user?.role === 'admin' && user?.admin_type === 'super_admin');

  // Was this admin explicitly assigned a Custom Role (Team & Roles >
  // Roles & Permissions)? Only true for that one restricted path — see
  // `login()`'s `is_restricted_team_member` assignment above. Consumed by
  // the sidebar to know when a `superAdminOnly` nav item's HARD block
  // should defer to the team member's actual granular grant instead
  // (a super admin may have explicitly handed them that one permission
  // via the Custom Role builder).
  const isRestrictedTeamMember = user?.is_restricted_team_member === true;
  
  // Check if user has access to a specific module
  // iter-230ak — Read through userRef so the identity is stable; consumers
  // (Sidebar, route guards) include this in their dep arrays. Previously
  // the `[user]` dep churned hasModuleAccess on every login / refresh,
  // which cascaded into Sidebar re-renders.
  const hasModuleAccess = useCallback(
    (module) => {
      const u = userRef.current;
      if (!u) return false;
      if (u.role !== 'admin' && u.role !== 'super_admin') return false;
      // Super admins, and any admin never explicitly assigned a Custom
      // Role, see everything — restriction only ever applies to team
      // members scoped to a specific role (Team & Roles > Roles &
      // Permissions). This keeps every pre-existing admin account
      // fully unaffected.
      if (!u.is_restricted_team_member) return true;
      const accessibleModules = u.accessible_modules || [];
      return accessibleModules.includes(module);
    },
    []
  );

  // Sub-module-level check (e.g. "finance" + "finance_register") for
  // restricted team members — falls through to `hasModuleAccess`'s same
  // unrestricted-by-default rule when the user isn't a scoped team member.
  const hasGranularAccess = useCallback(
    (moduleKey, subKey) => {
      const u = userRef.current;
      if (!u) return false;
      if (u.role !== 'admin' && u.role !== 'super_admin') return false;
      if (!u.is_restricted_team_member) return true;
      const granular = u.granular_permissions || {};
      if (!subKey) {
        return (u.accessible_modules || []).includes(moduleKey);
      }
      const prefix = `${moduleKey}.${subKey}`;
      return Object.keys(granular).some((k) => k === prefix || k.startsWith(`${prefix}.`));
    },
    []
  );
  
  // Check if user is currently impersonating a vendor
  const isImpersonating = useCallback(
    () => sessionStorage.getItem('impersonation_session_id') !== null,
    []
  );
  
  // End impersonation and return to admin account.
  // iter-232.J (RC-1) — Restore the admin token + user to ALL storage
  // tiers (sessionStorage, localStorage, IndexedDB). The previous
  // version only restored sessionStorage, leaving localStorage + IDB
  // with the stale vendor impersonation token — any future tab that
  // opened thereafter would rehydrate from localStorage and find
  // itself impersonating the wrong (or no) vendor.
  //
  // ALSO calls the backend /api/vendors/end-impersonation endpoint to
  // flip the impersonation_sessions row's is_active=false. Without this
  // call, the RC-1 backend safety net (refresh-token block while
  // impersonation row is active) would refuse to ever refresh the
  // admin session again, requiring a fresh login.
  const endImpersonation = useCallback(async () => {
    const adminToken = sessionStorage.getItem('admin_original_token')
      || localStorage.getItem('admin_original_token');
    const adminUserRaw = sessionStorage.getItem('admin_original_user')
      || localStorage.getItem('admin_original_user');
    const adminRefreshToken = sessionStorage.getItem('admin_original_refresh_token')
      || localStorage.getItem('admin_original_refresh_token');
    const sessionId = sessionStorage.getItem('impersonation_session_id')
      || localStorage.getItem('impersonation_session_id');

    if (!adminToken) return;

    // Close the DB-side impersonation row using the admin's original
    // token (NOT the current vendor impersonation token). Best-effort —
    // even if the call fails (e.g. network), we still restore local
    // admin state; the admin can re-end the session from the
    // VendorCredentialsTab audit panel later.
    if (sessionId && BACKEND_URL) {
      try {
        await fetch(`${BACKEND_URL}/api/vendors/end-impersonation`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${adminToken}`,
          },
          body: JSON.stringify({ session_id: sessionId }),
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[endImpersonation] backend close failed; local state still restored:', e);
      }
    }

    // Restore admin auth across all tiers.
    persistAuth('token', adminToken);
    if (adminUserRaw) persistAuth('user', adminUserRaw);
    if (adminRefreshToken) persistAuth('refresh_token', adminRefreshToken);
    // Clear impersonation breadcrumbs from both stores.
    ['admin_original_token', 'admin_original_user',
     'admin_original_refresh_token', 'impersonation_session_id',
     'impersonating_vendor'].forEach((k) => {
      try { sessionStorage.removeItem(k); } catch (_e) { /* no-op */ }
      try { localStorage.removeItem(k); } catch (_e) { /* no-op */ }
    });
    window.location.href = '/admin/vendors';
  }, []);

  // Token refresh state to prevent race conditions
  const refreshingTokenRef = React.useRef(false);
  const refreshPromiseRef = React.useRef(null);
  const lastRefreshTimeRef = React.useRef(0);
  const REFRESH_COOLDOWN_MS = 5000; // 5 second cooldown between refreshes

  // Helper function to make authenticated API calls
  // Options:
  //   silentAuth: true → on 401 after refresh, return null instead of
  //               triggering logout + redirect. Use for non-critical
  //               background polling (notifications, dashboards) so a
  //               transient 401 can't yank the user out of the admin
  //               page they're actively editing.
  // iter-230h — Memoised with useCallback. Previously this was a plain
  // function declaration, so every AuthProvider render produced a NEW
  // reference. Consumer components (LiteDashboard, DashboardNew) put
  // `apiCall` in their useEffect / useCallback dep arrays → every render
  // re-fetched dashboard data → triggered storage / token effects → flicker
  // loop. Stabilising the reference here breaks the loop.
  // iter-230ak — Dep array is now empty: token is read through
  // `tokenRef.current` (kept in sync by a useEffect above) so `apiCall`
  // is stable for the entire lifetime of the provider. Previously the
  // `[token]` dep meant every login / refresh-rotation / cross-tab
  // adoption invalidated apiCall, cascading re-renders into every
  // useEffect that listed apiCall as a dep — the root of the 403 storm
  // on DashboardNew (3× fetchDashboardData fires × 2 parallel calls = 6
  // bursts).
  const apiCall = useCallback(async (endpoint, options = {}) => {
    const url = `${BACKEND_URL}${endpoint}`;
    const silentAuth = options.silentAuth === true;

    // iter-230ak — Short-circuit if this endpoint was 403'd recently.
    // Cloudflare's Managed Challenge will keep blocking until the user
    // solves it / the heuristic resets, so retrying within seconds just
    // floods the console and ingress. Surface a stable typed error
    // immediately so the UI can show a single retry CTA.
    const blockedUntil = forbiddenUntilRef.current.get(url) || 0;
    if (blockedUntil > Date.now()) {
      const err = new Error(getFriendlyError('403 forbidden'));
      err.code = 'FORBIDDEN_BACKOFF';
      err.endpoint = endpoint;
      throw err;
    }

    // iter-231B — Same pattern for 429 and 502/503/504 to prevent retry
    // storms on rate-limited or restarting backends. Short-circuit fast
    // with a typed error; the caller can surface it once and stop.
    const rateLimitedUntil = rateLimitedUntilRef.current.get(url) || 0;
    if (rateLimitedUntil > Date.now()) {
      const err = new Error('Rate limited — please wait a moment and retry.');
      err.code = 'RATE_LIMITED';
      err.endpoint = endpoint;
      err.retryAfterMs = rateLimitedUntil - Date.now();
      throw err;
    }
    const serverErrorUntil = serverErrorUntilRef.current.get(url) || 0;
    if (serverErrorUntil > Date.now()) {
      const err = new Error('The server is restarting. Please wait a moment and try again.');
      err.code = 'SERVER_UNAVAILABLE';
      err.endpoint = endpoint;
      err.retryAfterMs = serverErrorUntil - Date.now();
      throw err;
    }

    // Get token from ref (mirror of state), then sessionStorage, then
    // localStorage (cross-tab fallback). Reading through the ref keeps
    // `apiCall`'s identity stable across token rotations.
    let authToken = tokenRef.current || sessionStorage.getItem('token') || localStorage.getItem('token');
    
    // Helper to make the actual request
    const makeRequest = (tokenToUse) => {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const method = options.method || 'GET';
        
        xhr.open(method, url);
        xhr.withCredentials = true; // Include cookies for refresh token

        // iter-230j — Explicit 30s client-side timeout. Without this XHR
        // sits open until the K8s ingress / Cloudflare kills the socket
        // at 100-120s, which manifests to the user as a multi-minute
        // "frozen / flickering" page with no clear error. 30s is well
        // under any upstream timeout and gives a deterministic, typed
        // error the UI can show a retry CTA for.
        const REQUEST_TIMEOUT_MS = options.timeoutMs || 30000;
        xhr.timeout = REQUEST_TIMEOUT_MS;
        xhr.ontimeout = () => {
          console.warn(`[apiCall] Timeout (${REQUEST_TIMEOUT_MS}ms):`, url);
          const err = new Error(
            'The server is taking longer than expected. Please check your connection and retry.'
          );
          err.code = 'TIMEOUT';
          err.endpoint = endpoint;
          reject(err);
        };
        
        // Don't set Content-Type for FormData - browser sets it automatically with boundary
        const isFormData = options.body instanceof FormData;
        if (!isFormData) {
          xhr.setRequestHeader('Content-Type', 'application/json');
        }
        
        if (tokenToUse) {
          xhr.setRequestHeader('Authorization', `Bearer ${tokenToUse}`);
        }

        // iter-230p — Honour an optional AbortController signal so
        // callers (e.g. useOnboardingArtifacts) can kill stale in-flight
        // requests when the user identity changes. Without this an
        // XHR fired for user A could resolve AFTER user B has logged
        // in and overwrite B's state with A's verdict.
        if (options.signal) {
          if (options.signal.aborted) {
            xhr.abort();
            const err = new Error('Request aborted');
            err.name = 'AbortError';
            reject(err);
            return;
          }
          options.signal.addEventListener('abort', () => {
            try { xhr.abort(); } catch (_) { /* ignore */ }
            const err = new Error('Request aborted');
            err.name = 'AbortError';
            reject(err);
          }, { once: true });
        }
        
        // Add any custom headers (skip Content-Type if it's FormData or already set above)
        if (options.headers && typeof options.headers === 'object' && !Array.isArray(options.headers)) {
          Object.keys(options.headers).forEach(key => {
            // Skip Content-Type — it's set above for non-FormData requests.
            // XHR.setRequestHeader appends duplicates (unlike fetch which replaces),
            // producing "application/json, application/json" which FastAPI/Pydantic
            // does not recognise as JSON → body is passed as raw bytes → 422.
            if (key.toLowerCase() === 'content-type') return;
            xhr.setRequestHeader(key, options.headers[key]);
          });
        }
        
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);
              // Dispatch custom event to signal API activity (for session tracking)
              window.dispatchEvent(new CustomEvent('api-activity'));
              resolve({ success: true, data });
            } catch (e) {
              window.dispatchEvent(new CustomEvent('api-activity'));
              resolve({ success: true, data: xhr.responseText });
            }
          } else if (xhr.status === 401) {
            resolve({ success: false, status: 401 });
          } else if (xhr.status === 403) {
            // Forbidden - user doesn't have permission OR upstream
            // (Cloudflare) is blocking the request. Record a per-URL
            // suppression window so subsequent renders don't re-fire
            // the same blocked call and create a 403 storm in the
            // console / ingress logs.
            try {
              forbiddenUntilRef.current.set(url, Date.now() + FORBIDDEN_BACKOFF_MS);
            } catch (_) { /* Map.set never throws, but be defensive */ }
            console.warn('[apiCall] Access forbidden:', url);
            const err = new Error(getFriendlyError('403 forbidden'));
            err.code = 'FORBIDDEN';
            err.endpoint = endpoint;
            reject(err);
          } else if (xhr.status === 409) {
            // iter-230j — Surface the structured detail (e.g.
            // ONBOARDING_INCOMPLETE) so callers can route on it instead
            // of just toasting a friendly string.
            try {
              const conflict = JSON.parse(xhr.responseText);
              const detail = conflict?.detail || conflict;
              const err = new Error(
                typeof detail === 'object'
                  ? detail.message || 'Conflict'
                  : String(detail)
              );
              err.code = (typeof detail === 'object' && detail.code) || 'CONFLICT';
              err.detail = detail;
              err.status = 409;
              reject(err);
            } catch (_) {
              reject(new Error(getFriendlyError('HTTP 409: Conflict')));
            }
          } else if (xhr.status === 429) {
            // iter-231B — Honor Retry-After (seconds or HTTP-date).
            // Suppress further calls to this URL until the server says
            // it's safe. Never retry automatically.
            let retryAfterMs = RATE_LIMIT_DEFAULT_MS;
            try {
              const ra = xhr.getResponseHeader('Retry-After');
              if (ra) {
                const asNum = Number(ra);
                if (!Number.isNaN(asNum) && asNum > 0) {
                  retryAfterMs = Math.min(asNum * 1000, 5 * 60 * 1000);
                } else {
                  const asDate = Date.parse(ra);
                  if (!Number.isNaN(asDate)) {
                    retryAfterMs = Math.max(1000, asDate - Date.now());
                  }
                }
              }
            } catch (_) { /* defensive */ }
            try {
              rateLimitedUntilRef.current.set(url, Date.now() + retryAfterMs);
            } catch (_) { /* ignore */ }
            console.warn(`[apiCall] Rate limited (429), backing off ${retryAfterMs}ms:`, url);
            const err = new Error('Rate limited — please wait a moment and retry.');
            err.code = 'RATE_LIMITED';
            err.endpoint = endpoint;
            err.status = 429;
            err.retryAfterMs = retryAfterMs;
            reject(err);
          } else if (xhr.status === 502 || xhr.status === 503 || xhr.status === 504) {
            // iter-231B — Backend is restarting / unavailable. Suppress
            // for SERVER_ERROR_BACKOFF_MS so refetch loops don't pile
            // up while the pod comes back. Honor Retry-After if sent.
            let backoffMs = SERVER_ERROR_BACKOFF_MS;
            try {
              const ra = xhr.getResponseHeader('Retry-After');
              if (ra) {
                const asNum = Number(ra);
                if (!Number.isNaN(asNum) && asNum > 0) {
                  backoffMs = Math.min(asNum * 1000, 60 * 1000);
                }
              }
            } catch (_) { /* defensive */ }
            try {
              serverErrorUntilRef.current.set(url, Date.now() + backoffMs);
            } catch (_) { /* ignore */ }
            console.warn(`[apiCall] Server unavailable (${xhr.status}), backing off ${backoffMs}ms:`, url);
            const err = new Error('The server is restarting. Please wait a moment and try again.');
            err.code = 'SERVER_UNAVAILABLE';
            err.endpoint = endpoint;
            err.status = xhr.status;
            err.retryAfterMs = backoffMs;
            reject(err);
          } else {
            try {
              const error = JSON.parse(xhr.responseText);
              // iter-229d-fix: FastAPI 400/422 sometimes carries a
              // structured `detail` object (e.g., {message, error_code,
              // missing_fields, action}). Flatten to a readable string so
              // the toast doesn't render "[object Object]".
              let rawMsg = error.detail || error.message || `HTTP ${xhr.status}`;
              if (rawMsg && typeof rawMsg === 'object') {
                const parts = [];
                if (rawMsg.message) parts.push(rawMsg.message);
                if (Array.isArray(rawMsg.missing_fields) && rawMsg.missing_fields.length) {
                  parts.push(`Missing: ${rawMsg.missing_fields.join(', ')}.`);
                }
                if (rawMsg.action) parts.push(rawMsg.action);
                // iter-263: 424 Shopify errors carry detail.body with the
                // actual Shopify API error text (e.g. "SHOPIFY_CALCULATE_FAILED").
                // Extract code + body so admins see the real reason.
                if (rawMsg.code) parts.unshift(rawMsg.code);
                if (rawMsg.body && typeof rawMsg.body === 'string') parts.push(rawMsg.body);
                rawMsg = parts.length ? parts.join(' — ') : JSON.stringify(rawMsg);
              }
              console.warn(`[apiCall] Error ${xhr.status}:`, rawMsg);
              reject(new Error(getFriendlyError(rawMsg)));
            } catch (e) {
              const rawMsg = `HTTP ${xhr.status}: ${xhr.statusText}`;
              console.warn('[apiCall] Unparseable error:', rawMsg);
              reject(new Error(getFriendlyError(rawMsg)));
            }
          }
        };
        
        xhr.onerror = () => {
          console.error('[apiCall] Network error:', url);
          reject(new Error(getFriendlyError('Failed to fetch')));
        };
        
        // Send request body if provided
        if (options.body) {
          xhr.send(options.body);
        } else {
          xhr.send();
        }
      });
    };
    
    // Try to refresh token (with lock to prevent race conditions)
    const tryRefreshToken = async () => {
      // If already refreshing, wait for that promise
      if (refreshingTokenRef.current && refreshPromiseRef.current) {
        return refreshPromiseRef.current;
      }
      
      // iter-231B — Refresh-token rate-limit shield. Production logs
      // showed `refresh-token → 429` cycling at sub-second intervals
      // because every parallel 401 in the dashboard triggered its own
      // refresh attempt. The cooldown above already serializes refreshes
      // within REFRESH_COOLDOWN_MS, but if the cooldown elapses while
      // the server is still in a 429 backoff window we'd re-bang the
      // door. Stamp a separate `refreshTokenBlockedUntilRef` window so
      // we hard-stop refresh calls during a rate-limit period.
      const refreshUrl = `${BACKEND_URL}/api/security/refresh-token`;
      const refreshBlockedUntil = rateLimitedUntilRef.current.get(refreshUrl) || 0;
      if (refreshBlockedUntil > Date.now()) {
        // eslint-disable-next-line no-console
        console.warn(`[Auth] refresh-token rate-limited, skipping for ${refreshBlockedUntil - Date.now()}ms`);
        return sessionStorage.getItem('token');
      }

      // Cooldown check — if we refreshed recently, use current token from sessionStorage
      const now = Date.now();
      if (now - lastRefreshTimeRef.current < REFRESH_COOLDOWN_MS) {
        return sessionStorage.getItem('token');
      }
      
      // Set lock
      refreshingTokenRef.current = true;
      
      refreshPromiseRef.current = (async () => {
        try {
          // iter-232.J (RC-1) — CRITICAL: refuse to refresh while the
          // current user is impersonating. The refresh-token endpoint
          // uses the admin's HttpOnly cookie (or admin's body fallback)
          // and would mint a fresh super_admin access token, silently
          // destroying the vendor impersonation session — producing the
          // 403 storm on vendor-subscription/* and vendor-locations/*
          // endpoints that was reported on production. When the
          // impersonation access token expires (or any 401 fires), we
          // simply notify the app shell and let the admin choose to
          // return to admin context or re-impersonate cleanly.
          const currentUser = userRef.current;
          if (currentUser && currentUser.is_impersonation === true) {
            // eslint-disable-next-line no-console
            console.warn('[Auth] refresh-token skipped: active impersonation session');
            try {
              window.dispatchEvent(new CustomEvent('impersonation:expired', {
                detail: { reason: 'access_token_expired_or_unauthorized' },
              }));
            } catch (_e) { /* CustomEvent unsupported */ }
            return null;
          }

          // Send refresh_token in body as fallback for cross-origin (third-party cookie blocking)
          const storedRefreshToken = sessionStorage.getItem('refresh_token');
          const response = await fetch(`${BACKEND_URL}/api/security/refresh-token`, {
            method: 'POST',
            credentials: 'include', // Include cookies (refresh token is in HttpOnly cookie)
            headers: { 'Content-Type': 'application/json' },
            body: storedRefreshToken ? JSON.stringify({ refresh_token: storedRefreshToken }) : undefined
          });
          
          if (response.ok) {
            const data = await response.json();
            // Update token in state and BOTH stores so cross-tab views stay in sync
            setToken(data.access_token);
            persistAuth('token', data.access_token);
            // iter-230ae — Persist the rotated refresh token too. The
            // backend now mints a fresh refresh token on every refresh
            // with the full configured expiry (default 30d). For cross-
            // origin browsers that block 3rd-party cookies, this stored
            // copy is what gets sent back in the body the next time
            // refresh runs — without it we'd burn back to the original
            // (now-stale) refresh token after one rotation.
            if (data.refresh_token) {
              persistAuth('refresh_token', data.refresh_token);
            }
            // iter-refresh-status — merge fresh user fields (status, name)
            // returned by the backend so cached user.status reflects any
            // admin approval that happened since the last login.
            // Fixes: "Account Pending Approval" banner shown to approved vendors.
            if (data.user) {
              const storedRaw = sessionStorage.getItem('user') || localStorage.getItem('user');
              const storedUser = storedRaw ? JSON.parse(storedRaw) : {};
              const updatedUser = { ...storedUser, ...data.user };
              persistAuth('user', JSON.stringify(updatedUser));
              setUser(updatedUser);
            }
            lastRefreshTimeRef.current = Date.now();
            return data.access_token;
          }
          // Capture refresh failure reason for diagnostics
          const failureBody = await response.text().catch(() => '');
          // iter-231B — On 429 from refresh-token, seed the rate-limit
          // window so subsequent retries within REFRESH_TOKEN_RATE_LIMITED_MS
          // (default 10s) short-circuit cleanly instead of looping.
          if (response.status === 429) {
            try {
              const ra = response.headers.get('Retry-After');
              let ms = REFRESH_TOKEN_RATE_LIMITED_MS;
              if (ra) {
                const asNum = Number(ra);
                if (!Number.isNaN(asNum) && asNum > 0) {
                  ms = Math.min(asNum * 1000, 5 * 60 * 1000);
                }
              }
              rateLimitedUntilRef.current.set(refreshUrl, Date.now() + ms);
            } catch (_) { /* defensive */ }
          }
          // eslint-disable-next-line no-console
          console.warn(`[Auth] Refresh failed: status=${response.status} body=${failureBody.slice(0, 120)}`);
          return null;
        } catch (error) {
          console.error('Token refresh failed:', error);
          return null;
        } finally {
          // Release lock after cooldown period to prevent rapid-fire refreshes
          setTimeout(() => {
            refreshingTokenRef.current = false;
            refreshPromiseRef.current = null;
          }, REFRESH_COOLDOWN_MS);
        }
      })();
      
      return refreshPromiseRef.current;
    };
    
    // First attempt with current token
    const result = await makeRequest(authToken);
    
    if (result.success) {
      return result.data;
    }
    
    // If 401, try to refresh token and retry once (no further refresh attempts).
    // EXCEPTION: silentAuth callers (background pollers like the order
    // notification bell) must NOT trigger a refresh sequence at all.
    // Each poll cycle racing the refresh would generate the
    // "[Auth] 401 ... attempting refresh" log spam users have reported.
    // Treat silentAuth+401 as "session not ready / not interesting"; return
    // null so the poller backs off, and let the next foreground action
    // (e.g. clicking a real button) drive the actual refresh.
    if (result.status === 401 && silentAuth) {
      return null;
    }

    if (result.status === 401) {
      // eslint-disable-next-line no-console
      console.info(`[Auth] 401 on ${endpoint}, attempting refresh`);
      const newToken = await tryRefreshToken();

      if (newToken) {
        const retryResult = await makeRequest(newToken);

        if (retryResult.success) {
          return retryResult.data;
        }
        // Retry also failed — do NOT attempt another refresh
        // eslint-disable-next-line no-console
        console.warn(`[Auth] Retry after refresh still 401 on ${endpoint}`);
      }

      // Refresh failed or retry failed — log diagnostic + logout
      const storedToken = sessionStorage.getItem('token') || localStorage.getItem('token');
      const storedUser = sessionStorage.getItem('user') || localStorage.getItem('user');
      const reasonCode = !storedToken
        ? 'no_token'
        : !newToken
        ? 'refresh_failed'
        : 'retry_after_refresh_failed';
      // eslint-disable-next-line no-console
      console.warn(`[Auth] Session timeout — reason=${reasonCode} endpoint=${endpoint}`);

      // Silent-auth callers (background polling) do NOT trigger
      // logout + redirect. A transient 401 there can't knock the user
      // out of the page they're actively editing.
      if (silentAuth) {
        return null;
      }

      if (storedToken && storedUser) {
        // Token was stored but is invalid/expired - logout with proper redirect
        const userData = JSON.parse(storedUser);
        const wasAdmin = userData?.role === 'admin' || userData?.role === 'super_admin';

        // Dispatch session timeout event for toast notification
        window.dispatchEvent(new CustomEvent('session-timeout', {
          detail: {
            userType: wasAdmin ? 'admin' : 'vendor',
            reason: reasonCode,
            endpoint,
            message: 'Your session has expired. Please log in again.'
          }
        }));

        // iter-230ab — Soft-logout for vendors. Force-navigating to
        // /login mid-render produced the dashboard ↔ login flicker
        // (PrivateRoute saw `!user`, Login.jsx's mount-effect raced
        // storage-event rehydration in same-tab PWA WebViews). Now we
        // keep the vendor's page mounted and flip `sessionExpired=true`
        // so an inline banner can prompt re-auth without teleporting.
        // Admins keep the hard-redirect path because the admin shell
        // doesn't have the inline banner UX, and an admin's session is
        // higher stakes (audit trail, lockouts already in place).
        if (wasAdmin) {
          logout('/admin/login');
        } else {
          setSessionExpired(true);
          setToken(null);
          // Keep `user` set so the current page stays mounted; the
          // inline banner uses `user` to show "Welcome back, <name>"
          // copy. The next successful login() resets sessionExpired.
        }
      }
      throw new Error('Session expired');
    }
  }, []);

  // iter-230ak / iter-231B — Explicit-retry escape hatch from
  // 403/429/5xx backoff (used by error banners' "Retry" CTA). Without
  // a key, clears ALL backoffs; with an endpoint, clears just that
  // URL's suppression across every backoff map.
  const clearForbiddenBackoff = useCallback((endpoint) => {
    try {
      if (!endpoint) {
        forbiddenUntilRef.current.clear();
        rateLimitedUntilRef.current.clear();
        serverErrorUntilRef.current.clear();
      } else {
        const url = `${BACKEND_URL}${endpoint}`;
        forbiddenUntilRef.current.delete(url);
        rateLimitedUntilRef.current.delete(url);
        serverErrorUntilRef.current.delete(url);
      }
    } catch (_) { /* ignore */ }
  }, []);

  // iter-230h — Memoise the context value so consumers only re-render
  // when actual values change. Previously this object was recreated on
  // every render, cascading re-renders through every component using
  // `useAuth()` and contributing to the dashboard flicker loop.
  const contextValue = useMemo(
    () => ({
      user,
      token,
      login,
      logout,
      updateUser,
      isAdmin,
      isVendor,
      isSuperAdmin,
      isRestrictedTeamMember,
      hasModuleAccess,
      hasGranularAccess,
      isImpersonating,
      endImpersonation,
      loading,
      apiCall,
      // iter-230ab — Soft session-expired signal for vendors. Pages
      // can read this to show an inline "log back in" banner instead
      // of being teleported to /login mid-render.
      sessionExpired,
      clearSessionExpired: () => setSessionExpired(false),
      // iter-230ak — Retry CTAs call this to bypass the per-endpoint
      // 403 backoff after the user has (e.g.) solved a Cloudflare
      // challenge in another tab.
      clearForbiddenBackoff,
    }),
    [
      user,
      token,
      loading,
      isAdmin,
      isVendor,
      isSuperAdmin,
      isRestrictedTeamMember,
      hasModuleAccess,
      hasGranularAccess,
      isImpersonating,
      endImpersonation,
      apiCall,
      sessionExpired,
      clearForbiddenBackoff,
    ]
  );

  return (
    <AuthContext.Provider
      value={contextValue}
    >
      {children}
    </AuthContext.Provider>
  );
};